import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calculator, TruckIcon, CheckCircle } from 'lucide-react';
import BackButton from '../components/BackButton';
import { useTranslation } from 'react-i18next';
import { getTarifsData } from '../data/tarifsData';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

export default function Tarifs() {
  const { t, i18n } = useTranslation();
  const { betonTypes, prixTiers } = getTarifsData(i18n.language);

  return (
    <>
      <SEO 
        title="Prix Béton Imprimé au m² - Tarifs 2026 | DumaExpertBéton"
        description="Découvrez nos tarifs béton imprimé dès 73€/m², béton désactivé, balayé et lissé. Prix transparents, devis gratuit. Zone PACA, Vaucluse."
        url="https://duma-expert-beton.fr/tarifs"
      />

      <div className="fixed top-4 left-4 z-50">
        <BackButton />
      </div>
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto pt-12">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-terracotta-500 to-clay-500 rounded-full">
              <span className="text-white font-semibold">{t('tarifs.badge')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              {t('tarifs.title')}
            </h1>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto">
              {t('tarifs.subtitle')}
            </p>
          </div>

          {/* Prix Béton avec Tiers */}
          <Card className="bg-slate-800 border-slate-700 mb-8">
            <CardHeader>
              <CardTitle className="text-2xl text-slate-100 flex items-center gap-2">
                {t('tarifs.pricingTitle')}
              </CardTitle>
              <CardDescription className="text-slate-200">
                {t('tarifs.pricingSubtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {prixTiers.map((tier, index) => (
                  <div 
                    key={index}
                    className="p-6 rounded-xl border-2 bg-slate-900 border-slate-700 transition-all hover:scale-105 hover:border-terracotta-500"
                  >
                    <div className="text-center">
                      <div className="text-sm text-slate-200 mb-2">{tier.label}</div>
                      <div className="text-3xl font-bold text-orange-400 mb-2">{tier.prix}</div>
                      <div className="text-white font-semibold">{tier.range}</div>
                      {index === 1 && (
                        <div className="mt-3 inline-block px-3 py-1 bg-terracotta-600 text-white text-xs rounded-full font-semibold">
                          {t('tarifs.popularBadge')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
                <p className="text-blue-300 text-sm">
                  💡 <strong>{t('tarifs.volumeAdvantage')}</strong> {t('tarifs.volumeText')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Types de Béton */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              {t('tarifs.typesTitle')}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {betonTypes.map((type, index) => (
                <Card key={index} className="bg-slate-800 border-slate-700 hover:border-terracotta-600 transition-colors">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <span className="text-2xl">{type.icon}</span>
                      {type.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-200">{type.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Services Complémentaires */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-xl text-white flex items-center gap-2">
                  {t('tarifs.terasamentTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-slate-200">{t('tarifs.terasamentPrice')}</span>
                  <span className="text-3xl font-bold text-orange-400">33€/m²</span>
                </div>
                <div className="space-y-2 text-sm text-slate-200">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('tarifs.terasamentFeature1')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('tarifs.terasamentFeature2')}</span>
                  </div>
                  <div className="mt-3 p-2 bg-green-900/20 border border-green-700 rounded">
                    <p className="text-xs text-green-300">
                      ✅ {t('tarifs.terasamentNote')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-xl text-white flex items-center gap-2">
                  <TruckIcon className="w-5 h-5 text-terracotta-400" />
                  {t('tarifs.pumpTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-200">{t('tarifs.pumpFixedCost')}</span>
                    <span className="text-2xl font-bold text-orange-400">500€</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-200">{t('tarifs.pumpPerM3')}</span>
                    <span className="text-2xl font-bold text-orange-400">+ 20€/m³</span>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-slate-200">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('tarifs.pumpFeature1')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('tarifs.pumpFeature2')}</span>
                  </div>
                  <div className="mt-3 p-2 bg-blue-900/20 border border-blue-700 rounded">
                    <p className="text-xs text-blue-300">
                      💡 {t('tarifs.pumpExample')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Exemple de Calcul */}
          <Card className="bg-gradient-to-br from-terracotta-900/20 to-clay-900/20 border-terracotta-700 mb-8">
            <CardHeader>
              <CardTitle className="text-2xl text-slate-100">{t('tarifs.exampleTitle')}</CardTitle>
              <CardDescription className="text-slate-200">
                {t('tarifs.exampleSubtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-slate-200 border-b border-slate-700 pb-2">
                  <span>{t('tarifs.exampleLine1')}</span>
                  <span className="font-semibold">13,200€</span>
                </div>
                <div className="flex justify-between text-slate-200 border-b border-slate-700 pb-2">
                  <span>{t('tarifs.exampleLine2')}</span>
                  <span className="font-semibold">4,950€</span>
                </div>
                <div className="flex justify-between text-slate-200 border-b border-slate-700 pb-2">
                  <span>{t('tarifs.exampleLine3')}</span>
                  <span className="font-semibold">800€</span>
                </div>
                <div className="flex justify-between text-xl font-bold pt-2">
                  <span className="text-slate-100">{t('tarifs.totalEstimated')}</span>
                  <span className="text-green-400">18,950€</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA Section */}
          <div className="text-center space-y-4">
            <h3 className="text-2xl font-bold text-slate-100">
              {t('tarifs.ctaTitle')}
            </h3>
            <p className="text-slate-200 max-w-2xl mx-auto">
              {t('tarifs.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/devis">
                <Button className="bg-gradient-to-r from-terracotta-500 to-clay-500 hover:from-terracotta-600 hover:to-clay-600 text-white text-lg px-8 py-6">
                  <Calculator className="w-5 h-5 mr-2" />
                  {t('tarifs.ctaButton')}
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white text-lg px-8 py-6">
                  {t('tarifs.ctaContact')}
                </Button>
              </Link>
            </div>
          </div>

          {/* Notes */}
          <div className="mt-12 p-6 bg-slate-800/50 border border-slate-700 rounded-xl">
            <h4 className="font-semibold text-white mb-3">{t('tarifs.notesTitle')}</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>• {t('tarifs.note1')}</li>
              <li>• {t('tarifs.note2')}</li>
              <li>• {t('tarifs.note3')}</li>
              <li>• {t('tarifs.note4')}</li>
              <li>• {t('tarifs.note5')}</li>
            </ul>
          </div>

        </div>
      </div>
    </>
  );
}
